/**
 * Export utilities for CSV and Excel file generation
 */

// Declare document for browser environment detection
declare const document: any;

/**
 * Convert array of objects to CSV string
 */
function convertToCSV(data: any[]): string {
  if (!data || data.length === 0) return '';

  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(',')];

  for (const row of data) {
    const values = headers.map((header) => {
      const value = row[header];
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') return JSON.stringify(value).replace(/"/g, '""');
      return `"${String(value).replace(/"/g, '""')}"`;
    });
    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
}

/**
 * Download data as CSV file
 * Note: This function only works in browser environments
 */
export function downloadCSV(data: any[], filename: string): void {
  if (typeof document === 'undefined') {
    throw new Error('downloadCSV can only be used in browser environments');
  }
  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Download data as Excel file using simple HTML table approach
 * This creates a .xls file that Excel can open (not true .xlsx)
 * Note: This function only works in browser environments
 */
export function downloadExcel(data: any[], filename: string): void {
  if (typeof document === 'undefined') {
    throw new Error('downloadExcel can only be used in browser environments');
  }
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);

  // Create HTML table
  let html = '<html><head><meta charset="utf-8"></head><body><table>';

  // Header row
  html += '<tr>';
  headers.forEach((header) => {
    html += `<th>${header}</th>`;
  });
  html += '</tr>';

  // Data rows
  data.forEach((row) => {
    html += '<tr>';
    headers.forEach((header) => {
      const value = row[header];
      let cellValue = '';
      if (value !== null && value !== undefined) {
        cellValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      }
      html += `<td>${cellValue}</td>`;
    });
    html += '</tr>';
  });

  html += '</table></body></html>';

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.xls') ? filename : `${filename}.xls`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Get unit suffix for display (B = billion, M = million, K = thousand, T = trillion)
 */
function getUnitSuffix(unit: string | null | undefined): string {
  if (!unit) return '';

  const unitLower = unit.toLowerCase();
  switch (unitLower) {
    case 'trillion':
    case 'trillions':
      return 'T';
    case 'billion':
    case 'billions':
      return 'B';
    case 'million':
    case 'millions':
      return 'M';
    case 'thousand':
    case 'thousands':
      return 'K';
    case 'number':
    case 'count':
    case '%':
    case 'percent':
      return '';
    default:
      return '';
  }
}

/**
 * Format metric value for display and export
 */
export function formatMetricValue(value: any): string {
  if (!value) return '';
  if (typeof value === 'object') {
    if (value.amount !== undefined) {
      const currency = value.currency || 'USD';
      const unit = value.unit;

      // Get unit suffix for display
      const unitSuffix = getUnitSuffix(unit);

      // Format the number with appropriate decimal places
      // Show decimals for scaled units to preserve precision (e.g., $1.45M instead of $1M)
      const hasScaledUnit = unitSuffix !== '';

      let formattedValue: string;
      try {
        formattedValue = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: hasScaledUnit ? 2 : 0,
        }).format(value.amount);
      } catch (error) {
        // Fallback if currency code is invalid
        formattedValue = `${currency}${value.amount.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: hasScaledUnit ? 2 : 0,
        })}`;
      }

      // Append unit suffix if present
      return unitSuffix ? `${formattedValue}${unitSuffix}` : formattedValue;
    }
    if (value.percentage !== undefined) {
      return `${value.percentage}%`;
    }
    if (value.value !== undefined) {
      return String(value.value);
    }
    if (value.months !== undefined) {
      return value.months ? `${value.months} months` : '';
    }
    return JSON.stringify(value);
  }
  return String(value);
}
